import { useState, useEffect, useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import { OciAiTestDto } from '../../../dto/OciAiTestDto';
import * as ociAiTestApi from '../../../apis/oci-ai-test';

/**
 * OCI AI 테스트 채팅 페이지 Hook
 * 
 * @description 채팅 페이지의 상태 관리 및 비즈니스 로직
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-20
 */
export const useChatPage = () => {
  // 상태 관리
  // 기본 모델을 xai.grok-3로 설정 (OCI 환경에서 실제 사용 가능한 모델)
  const [selectedModel, setSelectedModel] = useState<string>('xai.grok-3');
  const [messages, setMessages] = useState<OciAiTestDto.ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [availableModels, setAvailableModels] = useState<OciAiTestDto.ModelInfo[]>([]);
  
  // 세션 UUID 상태 (DB 저장용)
  const [sessionUuid, setSessionUuid] = useState<string | undefined>(undefined);
  
  // 고급 옵션 기본값
  const [advancedOptions, setAdvancedOptions] = useState<OciAiTestDto.AdvancedOptions>({
    temperature: 0.7,
    max_tokens: 2000,
    top_p: 0.95,
    top_k: 40,
    frequency_penalty: 0,
    presence_penalty: 0,
  });

  // 사용 가능한 모델 목록 로드
  const loadAvailableModels = useCallback(async () => {
    try {
      console.log('[COMPONENT]', {
        name: 'useChatPage',
        action: 'loadAvailableModels',
        timestamp: new Date().toISOString(),
      });

      const response = await ociAiTestApi.getAvailableModels();
      
      console.log('[COMPONENT]', {
        name: 'useChatPage',
        action: 'loadAvailableModels',
        responseSuccess: response.success,
        responseMessage: response.message,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
        dataLength: Array.isArray(response.data) ? response.data.length : 0,
      });

      if (response.success && Array.isArray(response.data)) {
        setAvailableModels(response.data);
        console.log('[COMPONENT]', {
          name: 'useChatPage',
          action: 'loadAvailableModels',
          result: 'success',
          modelsCount: response.data.length,
        });
      } else {
        console.error('[COMPONENT]', {
          name: 'useChatPage',
          action: 'loadAvailableModels',
          error: 'Invalid response structure',
          response,
        });
        enqueueSnackbar(
          response.message || 'Failed to load available models',
          { variant: 'error' }
        );
      }
    } catch (error: any) {
      console.error('[ERROR]', {
        component: 'useChatPage',
        action: 'loadAvailableModels',
        error: error.message,
        errorResponse: error.response?.data,
        errorStatus: error.response?.status,
        stack: error.stack,
      });

      let errorMessage = 'Error loading available models';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, []);

  // 메시지 전송
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    // 사용자 메시지 추가
    const userMessage: OciAiTestDto.ChatMessage = {
      role: 'user',
      content: content.trim(),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      // API 요청 데이터 구성 (sessionUuid 포함)
      const requestData: OciAiTestDto.ChatCompletionRequest = {
        messages: newMessages,
        sessionUuid: sessionUuid, // 기존 세션 UUID 사용 (없으면 새로 생성됨)
        modelId: selectedModel,
        ...advancedOptions,
      };

      console.log('[COMPONENT]', {
        name: 'useChatPage',
        action: 'sendMessage',
        sessionUuid: sessionUuid || 'new session',
        messageCount: newMessages.length,
      });

      // AI 응답 요청
      const response = await ociAiTestApi.chatCompletion(requestData);
      
      if (response.success) {
        // 세션 UUID 저장 (처음 응답받을 때 또는 변경될 때)
        if (response.data.sessionUuid && response.data.sessionUuid !== sessionUuid) {
          setSessionUuid(response.data.sessionUuid);
          console.log('[COMPONENT]', {
            name: 'useChatPage',
            action: 'sessionCreated',
            sessionUuid: response.data.sessionUuid,
            sessionId: response.data.sessionId,
          });
        }

        // AI 응답 메시지 추가
        const aiMessage: OciAiTestDto.ChatMessage = {
          role: 'assistant',
          content: response.data.text,
        };

        setMessages(prev => [...prev, aiMessage]);
        
        // 성공 메시지 표시 (응답 시간 포함)
        if (response.data.responseTime) {
          enqueueSnackbar(
            `Response received in ${response.data.responseTime}ms (Session: ${response.data.sessionId})`, 
            { variant: 'success' }
          );
        }
      } else {
        enqueueSnackbar(response.message || 'Failed to get AI response', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      enqueueSnackbar('Error sending message to AI model', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [messages, selectedModel, advancedOptions, isLoading, sessionUuid]);

  // 채팅 초기화 (세션도 초기화)
  const clearMessages = useCallback(() => {
    setMessages([]);
    setSessionUuid(undefined); // 세션 UUID 초기화 (새 세션 시작)
    console.log('[COMPONENT]', {
      name: 'useChatPage',
      action: 'clearMessages',
      message: 'Session reset',
    });
  }, []);

  // 컴포넌트 마운트 시 모델 목록 로드
  useEffect(() => {
    loadAvailableModels();
  }, [loadAvailableModels]);

  return {
    // 상태
    selectedModel,
    setSelectedModel,
    messages,
    setMessages,
    isLoading,
    availableModels,
    advancedOptions,
    setAdvancedOptions,
    
    // 액션
    sendMessage,
    clearMessages,
    loadAvailableModels,
  };
};